import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { Subject, debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { User } from './model/user';
import { UserManagementService } from '../../services/user-management.service';
import { Pagination } from './model/pagination.model';
import { PaginatedUserResponse } from './model/paginated-user.model';
import { UpdateCurrentPaginatedUsersAction } from '../../../../shared/state/UserManagementState';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { DealerState, UpdateGroupsAction } from '../../../../shared/state/DealerState';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { UserFilters } from './model/user-filters.model';
import { CustomModalPopupComponent } from '../../../../shared/components/custom-modal-popup/custom-modal-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { FeatureflagsService } from 'src/app/shared/services/featureflags.service';
import { ADMIN_GROUP_NAME, FEATURE_FLAGS } from 'src/app/shared/constants/constants';
import { environment } from '../../../../../environments/environment';
import { hasRolePermission } from '../../utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';

declare const gtag: (
  type: string,
  name: string,
  options: {
    event_category: string;
    event_label: string;
    value: string;
  }
) => void;

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  standalone: false
})
export class UserComponent implements OnInit, AfterViewInit, OnDestroy {
  users: User[] = [];
  pagination: Pagination;
  selection: User[] = [];
  pageSizeOptions = [10, 20, 50, 100];
  currentPage = 1;
  pageSize = 10;
  dealer: Dealer;
  username: string;
  resetPasswordConfirmationText: string;
  resetPasswordText: string;
  groups: any[] = [];
  showTblHiddenColumns: boolean = false;
  private readonly paginationStoragePrefix = 'user-table-';
  private readonly destroy$ = new Subject<void>();
  private nextNavigationUrl = '';

  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('resetPasswordModal', { static: true }) resetPasswordModal: ModalComponent;
  @ViewChild('resetPasswordConfirmation', { static: true }) resetPasswordContentModal: TemplateRef<any>;
  public alertService = inject(AlertService);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public dialog = inject(MatDialog);
  public featureflagsService = inject(FeatureflagsService);
  public userManagementService = inject(UserManagementService);
  public formBuilder = inject(UntypedFormBuilder);
  public route = inject(ActivatedRoute);
  public router = inject(Router);

  tableConfig: TableConfig<User> = {
    paginator: {
      style: 'pageNumber',
      options: {
        nbPagesToShow: 5
      },
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    onRowClick: (user: User) => {
      this.navigateToUser(user);
    },
    storage: {
      type: sessionStorage,
      options: {
        prefix: this.paginationStoragePrefix,
        keys: ['name', 'status', 'btmId', 'userGroup'],
        paginator: true,
        columns: false,
        tags: false
      }
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };

  getUserFilters(): UserFilters {
    const formValues = this.form.value;
    return {
      name: formValues.name || null,
      status: formValues.status || null,
      btmId: formValues.btmId || null,
      userGroup: formValues.userGroup ? formValues.userGroup.userGroupId : null
    };
  }

  fetcher: PaginatorFetcher<User> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    const userFilters = this.getUserFilters();
    return this.userManagementService.getUsers(this.dealer.id, userFilters, filters.page, filters.limit).pipe(
      map((userResponse: PaginatedUserResponse) => {
        if (userResponse != null && userResponse.error != null && userResponse.error.length > 0) {
          // TODO: change the dialog and content once the error dialog design is finalized
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          return;
        } else {
          this.users = userResponse.data.users;
          this.pagination = userResponse.data.pagination;
          this.store.dispatch(new UpdateCurrentPaginatedUsersAction(this.users));
          return {
            items: this.users,
            count: this.pagination.totalRecords
          };
        }
      })
    );
  };

  state = TableConfig.getState(this.tableConfig, this.route);

  form = this.formBuilder.group({
    name: [this.state.extraFilters?.name],
    status: [this.state.extraFilters?.status || 'Y'],
    btmId: [this.state.extraFilters?.btmId],
    userGroup: [this.state.extraFilters?.userGroup]
  });

  constructor() {
    this.resetPasswordConfirmationText = this.translationService.translate('restPasswordConfirmText') + '{user}?';
  }

  ngOnInit(): void {
    this.showTblHiddenColumns = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.SHOW_USRTBL_HIDDEN_COLS);
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.nextNavigationUrl = (event as NavigationStart).url;
      });
    this.form.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((values) => {
      this.tableRef.patchExtraFilters(values, true);
      // Track when user searches by name
      if (values.name && values.name.trim() !== '') {
        this.trackUserSearch('name', values.name);
      }
      if (values.userGroup) {
        this.trackUserSearch('userGroup', values.userGroup.groupName);
      }
      if (values.status) {
        this.trackUserSearch('status', values.status);
      }
      if (values.btmId) {
        this.trackUserSearch('btmId', values.btmId);
      }
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        login: {
          cell: (elm: User) => elm.username,
          header: this.translationService.translate('loginButtonText'),
          sortable: false,
          sticky: true
        },
        firstName: {
          cell: (elm: User) => elm.firstName,
          header: this.translationService.translate('firstNameText'),
          sortable: false
        },
        lastName: {
          cell: (elm: User) => elm.lastName,
          header: this.translationService.translate('lastNameText'),
          sortable: true
        },
        userGroup: {
          cell: (elm: User) => elm.userGroupName,
          header: this.translationService.translate('userGroupText'),
          sortable: false
        },
        locationType: {
          cell: (elm: User) => elm.locationType,
          header: this.translationService.translate('locationTypeText'),
          sortable: false
        },
        location: {
          cell: (elm: User) => elm.location,
          header: this.translationService.translate('locationText'),
          sortable: false
        },
        status: {
          cell: 'activeCell',
          header: this.translationService.translate('statusColumnText'),
          sortable: false
        },
        UserLocked: {
          cell: 'userLockedCell',
          header: this.translationService.translate('userLockedText'),
          sortable: false
        },
        UserExpired: {
          cell: 'userExpiredCell',
          header: this.translationService.translate('userPasswordExpiredText'),
          sortable: false
        }
      };
      if (this.showTblHiddenColumns) {
        this.tableConfig.columns.createdOn = {
          cell: 'createdOnCell',
          header: this.translationService.translate('createdOnColumnText'),
          sortable: false
        };
        this.tableConfig.columns.lastLogin = {
          cell: 'lastLoginCell',
          header: this.translationService.translate('lastLoginText'),
          sortable: false
        };
      }
      this.tableConfig.columns = {
        ...this.tableConfig.columns,
        btmId: {
          cell: (elm: User) => elm.btmId,
          header: this.translationService.translate('btmIdText'),
          sortable: false
        }
      };
      if (this.showTblHiddenColumns) {
        this.tableConfig = {
          ...this.tableConfig,
          columns: this.tableConfig.columns || {},
          onSelectionChange: (elms: User[]) => console.log('Selection change', elms),
          selection: {
            columnKeyForCheckboxLabel: 'name',
            allLabel: 'All'
          }
        };
      }
    });

    this.fetchUserGroups();
  }

  ngAfterViewInit(): void {
    // mic-table's page-number paginator shows page 1 after the first data load even when a later page is restored.
    this.tableRef?.paginator?.dataChanged.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(() => this.syncPaginatorPageIndicator());
    });
  }

  private syncPaginatorPageIndicator(): void {
    const paginator = this.tableRef?.paginator;
    const targetIndex = paginator?.pageIndex ?? 0;
    if (!paginator || !targetIndex) {
      return;
    }
    const paginatorRefs = [paginator.paginatorRef1, paginator.paginatorRef2].filter(Boolean);
    paginatorRefs.forEach((ref) => {
      ref.pageIndex = 0;
    });
    setTimeout(() => {
      paginatorRefs.forEach((ref) => {
        ref.pageIndex = targetIndex;
      });
    });
  }

  ngOnDestroy(): void {
    // Keep filters/page only when heading into a user detail page; clear on any other navigation.
    if (!/^\/home\/setup\/usersetup\/user\/[^/?]+/.test(this.nextNavigationUrl)) {
      sessionStorage.removeItem(`${this.paginationStoragePrefix}state`);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  showResetPasswordModal(item: User) {
    this.username = item.username;
    this.resetPasswordText = this.resetPasswordConfirmationText.replace('{user}', '<strong>' + this.username + '</strong>');
    const modalWidth = Math.min(750, Math.max(280, (window?.innerWidth || 750) - 32));
    this.resetPasswordModal.open({
      id: item.username,
      width: modalWidth,
      title: this.translationService.translate('resetPasswordText'),
      content: this.resetPasswordContentModal,
      confirm: {
        text: this.translationService.translate('resetPasswordText'),
        color: 'primary'
      },
      cancel: this.translationService.translate('cancelLinkText')
    });
  }

  onResetPasswordModalClosed(result: ModalResult) {
    if (result && result.result) {
      this.callResetPasswordService();
    }
  }

  resetFilters() {
    this.form.reset();
  }

  unlockUserAccount(data: User) {
    this.username = data.username;
    let dialogRef = this.dialog.open(CustomModalPopupComponent, { data: data.username });
    dialogRef.beforeClosed().subscribe((result: string) => {
      console.log(result);
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      dialogRef = null;
      if (result === 'unlock') {
        if (environment.envName !== 'dev') {
          // Sends an event to Google Analytics to track when the "Unlock User" button is clicked.
          // The event includes the username of the user whose account is being unlocked.
          gtag('event', 'unlock_user_clicked', {
            event_category: 'BUTTON_CLICK',
            event_label: 'Unlock User Click',
            value: `User Unlock Button Clicked: ${data.username}`
          });
        }
        this.callUnlockUserAccountService(data);
      } else if (result === 'reset') {
        if (environment.envName !== 'dev') {
          // Sends an event to Google Analytics to track when the "Reset User" button is clicked.
          // The event includes the username of the user whose password is being reset.
          gtag('event', 'reset_user_clicked', {
            event_category: 'BUTTON_CLICK',
            event_label: 'Reset User Click',
            value: `User Reset Button Clicked: ${data.username}`
          });
        }
        this.callResetPasswordService();
      }
    });
  }

  callUnlockUserAccountService(data: User) {
    this.userManagementService.unlockAccount(this.dealer.id, { username: data.username }).subscribe(
      (response) => {
        const isAccountUnlocked = response.data;
        const alertType = isAccountUnlocked ? AlertType.success : AlertType.critical;
        const alertTitle = isAccountUnlocked
          ? this.translationService.translate('unlockSuccessText')
          : this.translationService.translate('unlockFailText');
        const alertContent = isAccountUnlocked
          ? this.translationService.translate('confirmExistingUserText')
          : this.translationService.translate('unlockTechnicalErrorText');

        this.alertService.showAlert({ type: alertType, title: alertTitle, content: alertContent } as Alert);
      },
      (error) => {
        const alertContent =
          error.error.statusCode === 404
            ? error.error.message + '.' + this.translationService.translate('contactSupportText')
            : this.translationService.translate('unlockTechnicalErrorText');
        this.alertService.showAlert({
          type: AlertType.critical,
          title: this.translationService.translate('errorText'),
          content: alertContent
        } as Alert);
      }
    );
  }

  callResetPasswordService() {
    this.showChangePasswordModal();
  }

  fetchUserGroups(): Promise<void> {
    return new Promise((resolve) => {
      const groups = this.store.selectSnapshot(DealerState.groups);
      if (groups && groups.length > 0) {
        this.groups = groups.filter((group) => group.groupName?.toUpperCase() !== ADMIN_GROUP_NAME);
        this.syncRestoredUserGroup();
        resolve();
      } else {
        this.userManagementService.getUserGroups(this.dealer.id).subscribe((groups) => {
          const filteredGroups = groups.filter((group) => group.groupName?.toUpperCase() !== ADMIN_GROUP_NAME);
          this.store.dispatch(new UpdateGroupsAction(filteredGroups));
          this.groups = filteredGroups;
          this.syncRestoredUserGroup();
          resolve();
        });
      }
    });
  }

  // A restored userGroup is a deserialized copy; swap in the matching list item so the dropdown can select it.
  private syncRestoredUserGroup(): void {
    const selected = this.form.get('userGroup').value;
    const match = selected && this.groups.find((group) => group.userGroupId === selected.userGroupId);
    if (match && match !== selected) {
      this.form.get('userGroup').setValue(match, { emitEvent: false });
    }
  }

  isPasswordExpired(user: string) {
    return new Date(user) < new Date();
  }

  isAccountLocked(user: string) {
    return user === null;
  }

  addUser() {
    if (environment.envName !== 'dev') {
      // Sends an event to Google Analytics to track when the "Add User" button is clicked.
      gtag('event', 'add_user_clicked', {
        event_category: 'BUTTON_CLICK',
        event_label: 'Add User Click',
        value: `User Add Button Clicked`
      });
    }
    this.router.navigate(['/home/setup/usersetup/user'], { queryParams: { mode: 'add' } });
  }

  showChangePasswordModal() {
    let dialogRef = this.dialog.open(ChangePasswordComponent, {
      width: 'min(500px, calc(100vw - 2rem))',
      maxWidth: 'calc(100vw - 2rem)',
      data: {
        username: this.username,
        isResetPassword: true
      }
    });
    dialogRef.beforeClosed().subscribe((result: string) => {
      console.log(result);
      if (result === 'success') {
        this.alertService.showAlert({
          type: AlertType.success,
          title: this.translationService.translate('resetSuccessTitleText')
        } as Alert);
      } else if (result === 'failure') {
        this.alertService.showAlert({
          type: AlertType.critical,
          title: this.translationService.translate('resetFailText'),
          content: this.translationService.translate('resetTechnicalErrorText')
        } as Alert);
      } else {
        console.log(result);
      }
    });
    dialogRef.afterClosed().subscribe((result: string) => {
      console.log(result);
      dialogRef = null;
    });
  }

  // This method tracks user search actions and sends events to Google Analytics.
  // It logs the search field (e.g., name, user group, status) and the search value entered by the user.
  // The event helps monitor user behavior and search patterns in the application.
  private trackUserSearch(searchField: string, searchValue: string): void {
    if (environment.envName !== 'dev') {
      gtag('event', `search_user_by_${searchField}`, {
        event_category: 'USER_MANAGEMENT',
        event_label: `Search by ${searchField}`,
        value: `Dealer: ${this.dealer.id}, ${searchField} search: ${searchValue}`
      });
    }
  }

  navigateToUser(user: User) {
    this.router.navigateByUrl(`/home/setup/usersetup/user/${user.userId}`);
  }

  protected readonly hasRolePermission = hasRolePermission;
  protected readonly ROLES = ROLES;
}
